import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Keyboard } from 'lucide-react'

interface ShortcutEntry {
  keys: string[]
  labelKey: string
  fallback: string
}

const SHORTCUT_SECTIONS: { titleKey: string; fallbackTitle: string; shortcuts: ShortcutEntry[] }[] =
  [
    {
      titleKey: 'shortcuts.sectionGlobal',
      fallbackTitle: 'Global',
      shortcuts: [
        { keys: ['⌘', 'K'], labelKey: 'shortcuts.search', fallback: 'Search' },
        { keys: ['N'], labelKey: 'shortcuts.newContact', fallback: 'New contact' },
        { keys: ['?'], labelKey: 'shortcuts.help', fallback: 'Show shortcuts' },
        { keys: ['Esc'], labelKey: 'shortcuts.close', fallback: 'Close overlay' },
      ],
    },
    {
      titleKey: 'shortcuts.sectionNav',
      fallbackTitle: 'Navigation',
      shortcuts: [
        { keys: ['H'], labelKey: 'shortcuts.goHome', fallback: 'Go to Home' },
        { keys: ['C'], labelKey: 'shortcuts.goContacts', fallback: 'Go to Contacts' },
        { keys: ['R'], labelKey: 'shortcuts.goReminders', fallback: 'Go to Reminders' },
        { keys: ['S'], labelKey: 'shortcuts.goSettings', fallback: 'Go to Settings' },
        { keys: ['L'], labelKey: 'shortcuts.logInteraction', fallback: 'Log interaction' },
      ],
    },
  ]

function Kbd({ children }: { children: string }) {
  return (
    <kbd className="bg-surface-container-highest text-on-surface border-outline-variant/30 inline-flex h-6 min-w-6 items-center justify-center rounded-md border px-1.5 text-xs font-medium shadow-sm">
      {children}
    </kbd>
  )
}

interface KeyboardShortcutsHelpProps {
  open: boolean
  onClose: () => void
}

export function KeyboardShortcutsHelp({ open, onClose }: KeyboardShortcutsHelpProps) {
  const { t } = useTranslation()

  useEffect(() => {
    if (!open) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape' || e.key === '?') {
        e.preventDefault()
        e.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', handleKey, true)
    return () => document.removeEventListener('keydown', handleKey, true)
  }, [open, onClose])

  if (!open) return null

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-x-4 top-1/2 z-50 mx-auto max-w-md -translate-y-1/2 sm:inset-x-auto sm:w-full">
        <div className="bg-surface border-outline-variant/20 rounded-2xl border shadow-2xl">
          {/* Header */}
          <div className="border-outline-variant/20 flex items-center justify-between border-b px-5 py-4">
            <div className="flex items-center gap-2.5">
              <Keyboard className="text-primary h-5 w-5" />
              <h2 className="text-foreground text-base font-semibold">
                {t('shortcuts.title', 'Keyboard Shortcuts')}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-on-surface-variant hover:text-foreground rounded-lg p-1 transition-colors"
              aria-label={t('shortcuts.closeHelp', 'Close')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Shortcut list */}
          <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
            {SHORTCUT_SECTIONS.map((section, si) => (
              <div key={si} className={si > 0 ? 'mt-5' : ''}>
                <h3 className="text-on-surface-variant mb-2.5 text-xs font-semibold tracking-widest uppercase">
                  {t(section.titleKey, section.fallbackTitle)}
                </h3>
                <div className="space-y-2">
                  {section.shortcuts.map((shortcut, i) => (
                    <div key={i} className="flex items-center justify-between py-1">
                      <span className="text-foreground text-sm">
                        {t(shortcut.labelKey, shortcut.fallback)}
                      </span>
                      <div className="flex items-center gap-1">
                        {shortcut.keys.map((key, ki) => (
                          <Kbd key={ki}>{key}</Kbd>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer hint */}
          <div className="border-outline-variant/20 border-t px-5 py-3">
            <p className="text-on-surface-variant text-center text-xs">
              {t('shortcuts.hint', 'Press ? to toggle this help')}
            </p>
          </div>
        </div>
      </div>
    </>
  )
}
